import { ContentWrapper, Divider } from './styled';
import {
  InputDescription,
  InputError,
  InputTitle,
  InputWrapper,
} from 'components/atoms/styleds';
import Input from 'components/atoms/Input';
import { getErrorByKey } from 'utils';
import React, { useCallback, useEffect, useState } from 'react';
import BaseError from 'components/atoms/BaseError';
import Button from 'components/atoms/Button';
import Modal from 'components/molecules/Modal';
import ChipsList from 'components/molecules/ChipsList';
import { useDynamicApi } from 'hooks/api/dynamic-api';
import IpraticoApi from 'libs/api/ipratico';
import Select from 'components/atoms/Select';
import UIHelper from 'services/UIHelper';
import { StyledFlexbox } from 'components/atoms/Flexbox/styled';

const Ipratico = ({
  data,
  onChange,
  errors,
  settingsData = {},
  onUpdateSettings,
  hideSelect = false,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [fetchInProcess, setFetchInProcess] = useState(false);
  const [inProcess, setInProcess] = useState(false);
  const [ipraticoCategories, setIpraticoCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [serviceCharges, setServiceCharges] = useState([]);
  const [statistics, setStatistics] = useState({});
  const [autoImport, setAutoImport] = useState(
    !!settingsData?.ipratico_auto_update,
  );

  const { api } = useDynamicApi(IpraticoApi);

  const onClickImport = useCallback(async () => {
    setFetchInProcess(true);
    try {
      const {
        data: { categories },
      } = await api.list();
      setIpraticoCategories(
        categories.map((i) => ({ ...i, title: `${i.name}` })),
      );
      setSelectedCategories((s) => {
        const selectedCategories = [];
        categories?.forEach((i) => {
          if (i.selected) {
            selectedCategories.push(i.id);
          }
        });
        return [...s, ...selectedCategories];
      });
      setShowModal((s) => !s);
    } catch (e) {
    } finally {
      setFetchInProcess(false);
    }
  }, [onUpdateSettings, autoImport]);

  const onClose = useCallback(() => {
    setShowModal(false);
  }, []);

  const fetchStats = async () => {
    try {
      const { data } = await api.get('statistics');
      setStatistics(data);
    } catch (e) {}
  };

  useEffect(() => {
    (async () => {
      try {
        if (!settingsData.ipratico_key) {
          return;
        }
        const {
          data: { service_charges },
        } = await api.get('service_charges');
        setServiceCharges(service_charges);
      } catch (e) {}
    })();
  }, [api, settingsData]);

  useEffect(() => {
    fetchStats();
    const interval = setInterval(() => {
      fetchStats();
    }, 30000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  const onClickChip = useCallback((item) => {
    setSelectedCategories((state) => {
      const f = state.find((i) => i === item.id);
      if (f) {
        return state.filter((i) => i !== f);
      }
      return [...state, item.id];
    });
  }, []);

  const onImport = useCallback(async () => {
    setInProcess(true);
    try {
      await api.create({
        data: {
          category_ids: selectedCategories,
        },
      });
      onUpdateSettings &&
        onUpdateSettings({
          settings: {
            ipratico_auto_update: autoImport,
          },
        });
      fetchStats();
      UIHelper.showSuccessMessage();
      onClose();
      setTimeout(() => {
        fetchStats();
      }, 3000);
    } catch (e) {
      UIHelper.showRequestMessage(e?.response?.data);
    } finally {
      setInProcess(false);
    }
  }, [selectedCategories, onClose, autoImport, onUpdateSettings]);

  const onClickAutoImport = useCallback(() => {
    setAutoImport((s) => !s);
  }, []);

  return (
    <ContentWrapper>
      <InputTitle mt={0} mb={12}>
        Ipratico key
      </InputTitle>
      <Input
        name='ipratico_key'
        isMultiLang={false}
        fullWidth={false}
        standardWidth={true}
        value={data?.ipratico_key}
        label='Key'
        placeholder='fd234zxce23'
        onChange={onChange}
        error={
          getErrorByKey(errors, 'ipratico_key') || getErrorByKey(errors, 'ipratico_key')
        }
        mb={0}
      />
      {settingsData?.ipratico_key && !hideSelect && (
        <>
          <InputTitle mt={30} mb={12}>
            Ipratico charge id
          </InputTitle>
          <InputWrapper standardWidth={true} mt={20} desktopMt={20}>
            <Select
              onChange={onChange}
              name='ipratico_service_charge_id'
              label='Charge id'
              selectedKey={data?.ipratico_service_charge_id}
              defaultValue={data?.ipratico_service_charge_id}
              fullWidth={false}
              items={[
                { id: '', title: 'Charge id' },
                ...serviceCharges?.map((i) => ({ title: i.name, ...i })),
              ]}
              error={
                !data.ipratico_service_charge_id &&
                //eslint-disable-next-line
                "Selezionare per effettuare l'importazione"
              }
            />
          </InputWrapper>
          <Button
            onClick={onClickImport}
            color='action'
            flex={1}
            loading={fetchInProcess}
            disabled={!settingsData.ipratico_service_charge_id}
          >
            importa
          </Button>
        </>
      )}

      <div style={{ marginTop: 20 }}>
        <StyledFlexbox justify='start' align='center'>
          <InputTitle mb={0} style={{ marginRight: 8 }}>
            Categorie:{' '}
          </InputTitle>
          <InputDescription mb={0}>{statistics?.categories}</InputDescription>
        </StyledFlexbox>
        <StyledFlexbox justify='start' align='center'>
          <InputTitle mb={0} style={{ marginRight: 8 }}>
            Piatti:{' '}
          </InputTitle>
          <InputDescription mb={0}>{statistics?.dishes}</InputDescription>
        </StyledFlexbox>
        <StyledFlexbox justify='start' align='center'>
          <InputTitle mb={0} style={{ marginRight: 8 }}>
            Tavoli:{' '}
          </InputTitle>
          <InputDescription mb={0}>{statistics?.tables}</InputDescription>
        </StyledFlexbox>
        <StyledFlexbox justify='start' align='center'>
          <InputTitle mb={0} style={{ marginRight: 8 }}>
            Ingredienti:{' '}
          </InputTitle>
          <InputDescription mb={0}>{statistics?.options}</InputDescription>
        </StyledFlexbox>
      </div>
      <BaseError errors={errors} />
      <Modal
        variant='config'
        title='importa'
        onClose={onClose}
        onCancel={onClose}
        onSubmit={onImport}
        open={showModal}
        desktopMinH='40vh'
        inProcess={inProcess}
        desktopW='600'
        actionButtonText='importa'
        cancelButtonText='Indietro'
        body={
          <>
            <ChipsList
              name='category_ids[]'
              list={ipraticoCategories}
              selectedList={selectedCategories}
              onClick={onClickChip}
              error={null}
              disabledKeys={[]}
            />
            <Divider />
            <ChipsList
              name='auto_import'
              list={[{ title: 'Importa automaticamente', id: 'autoimport' }]}
              selectedList={autoImport ? ['autoimport'] : []}
              onlyNative={true}
              onClick={onClickAutoImport}
              error={null}
              isColumn={true}
              disabledKeys={[]}
            />
          </>
        }
      />
    </ContentWrapper>
  );
};

export default Ipratico;
